import React, { useCallback, useMemo } from "react";
import { ScrollView } from "react-native";
import { Mission, WorkerInfo, WorkingPermitStatus } from "../../__generated__/graphql";
import { useI18n } from "../../hooks/i18n-hooks";
import { Buildable } from "ts-essentials";
import { useQuery } from "@apollo/client";
import LoadingBoundary from "../../ui_atoms/LoadingBoundary";
import ListItem from "../../ui_atoms/ListItem";
import { GET_CLIENT_AND_INVITATION_WORKERS } from "../../graphql/client";
import { LabelMode } from "../../ui_atoms/Label";
import { useRelevantMissionCounter } from "../../hooks/missions";
import { useTheme } from "../../hooks/use-theme";

type WorkerInfoCellProps = {
    workerInfo: WorkerInfo;
    setWorkerInfo: (workerInfo: WorkerInfo) => void;
    selectedId?: string;
};

const WorkerInfoCell: React.FC<WorkerInfoCellProps> = ({
    workerInfo,
    setWorkerInfo,
    selectedId,
}) => {
    const i18n = useI18n();
    const isWorkerValid = !!workerInfo.address || !!workerInfo.birthdate;
    const isInvitation = "isInInvitation" in workerInfo;
    const getMissionCount = useRelevantMissionCounter();
    const [label, labelMode] = useMemo(() => {
        if (isInvitation) {
            return [
                i18n.t("screens.user.create_mission_worker.is_invitation"),
                "warning" as LabelMode,
            ];
        }
        if (workerInfo.workPermitStatus === WorkingPermitStatus.None) {
            return [i18n.t("screens.user.create_mission_worker.no_permit"), "default" as LabelMode];
        }
        if (workerInfo.workPermitStatus === WorkingPermitStatus.InProcess) {
            return [
                i18n.t("screens.user.create_mission_worker.permit_in_process"),
                "default" as LabelMode,
            ];
        }
        return [undefined, undefined];
    }, [i18n, isInvitation, workerInfo.workPermitStatus]);

    const onPress = useCallback(() => {
        setWorkerInfo(workerInfo);
    }, [setWorkerInfo, workerInfo]);

    return (
        <ListItem
            selectable
            key={workerInfo.id}
            title={workerInfo.name}
            useBigAvatarOrIcon
            icon={isInvitation ? "talent" : undefined}
            avatarPath={isInvitation ? undefined : workerInfo.avatarUrl}
            description={
                isInvitation
                    ? undefined
                    : i18n.t("screens.user.create_mission_worker.worker_card_description", {
                          count: getMissionCount(workerInfo.missionCount),
                          nbMissions: getMissionCount(workerInfo.missionCount),
                      })
            }
            labels={[{ text: label, mode: labelMode }]}
            disabled={!isWorkerValid}
            checked={workerInfo.id === selectedId}
            onPress={onPress}
        />
    );
};

type MissionWorkerProps = {
    mission: Buildable<Mission>;
    setWorkerInfo: (workerInfo: WorkerInfo) => void;
};

const MissionWorker: React.FC<MissionWorkerProps> = ({ mission, setWorkerInfo }) => {
    const { gridSize } = useTheme();
    const { loading, error, data } = useQuery(GET_CLIENT_AND_INVITATION_WORKERS, {
        fetchPolicy: "network-only",
    });
    const itemsObject = useMemo(() => {
        const result = [];
        if (data?.getClientWorkers) {
            result.push(...data.getClientWorkers);
        }
        if (data?.allInvitations.elements) {
            result.push(
                ...data.allInvitations.elements.map((invitation) => {
                    let name = "";
                    if (invitation.firstName) {
                        name = `${invitation.firstName}`;
                    }
                    if (invitation.lastName) {
                        name = `${name} ${invitation.lastName}`;
                    }
                    if (!name) {
                        name = invitation.phoneNumber;
                    }
                    return {
                        id: invitation.id,
                        name,
                        address: null,
                        birthdate: null,
                        missionCount: null,
                        missions: [],
                        isInInvitation: true,
                        deletedAt: false,
                    };
                }),
            );
        }
        return result;
    }, [data?.getClientWorkers, data?.allInvitations.elements]);

    return (
        <LoadingBoundary loading={loading} error={error}>
            <ScrollView style={{ overflow: "visible", marginHorizontal: -(gridSize / 3) }}>
                {itemsObject.map((workerInfo) => {
                    return (
                        <WorkerInfoCell
                            key={workerInfo.id}
                            workerInfo={workerInfo as WorkerInfo}
                            setWorkerInfo={setWorkerInfo}
                            selectedId={mission.workerInfo?.id}
                        />
                    );
                })}
            </ScrollView>
        </LoadingBoundary>
    );
};

export default MissionWorker;
